import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  useToast,
  Grid,
  Badge,
  IconButton,
  Select,
  Button,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { SearchIcon, EditIcon, ViewIcon } from "@chakra-ui/icons";
import { collection, collectionGroup, doc, getDocs, query, serverTimestamp, updateDoc, where, arrayUnion } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase/config";
import { stationIsConnected } from "./stationConnectionStatus";
import { useStore } from "../contexts/StoreContext";
import { useAuth } from "../contexts/AuthContext";
import { Store } from "../type/Store";
import { getPlanDisplayName, getPlanPrice } from "../../utils/subscription/planFeatures";
import { FiArrowDown, FiArrowUp, FiClock, FiLogIn } from "react-icons/fi";
import PageShell from "../layout/PageShell";
import MenuCopyTab from "./MenuCopyTab";
import CreateStoreDialog from "./CreateStoreDialog";
import { billingSubactivityOf } from "./createStoreRecord";
import { PRODUCT_VERSIONS_COLLECTION, ProductVersion, productVersionLabel } from "../../productVersions/types";

type StationDoc = {
  id: string;
  status?: string;
  connectionEnabled?: boolean;
  deviceSecretHash?: string;
  lastSeenAt?: { toDate?: () => Date; toMillis?: () => number } | null;
  stationName?: string;
  posStationId?: string;
  stationId?: string;
  fingerprint?: string;
};

type StationListRow = {
  key: string;
  name: string;
  stationId: string;
  fingerprint: string;
  sharedNames: string[];
  connected: boolean;
  overCapacity: boolean;
  loginPosUserId: string;
  loginPosUserName: string;
};

function shownStationId(item: { id?: string; posStationId?: string; stationId?: string }) {
  if (item.id && /^[0-9a-f]{16}$/.test(item.id)) return item.id;
  if (item.stationId && /^[0-9a-f]{16}$/.test(item.stationId)) return item.stationId;
  return item.posStationId || "";
}

function createdMillis(store: Store) {
  const created = store.created as { toMillis?: () => number; seconds?: number } | undefined;
  if (created && typeof created.toMillis === "function") return created.toMillis();
  if (created && typeof created.seconds === "number") return created.seconds * 1000;
  return 0;
}

const ACTIVITY_LABEL = {
  RES: "Restaurant",
  BAR: "Bar",
  CDR: "Food truck",
} as const;

function lastFour(value?: string) {
  const text = String(value || "").trim();
  if (!text || text === "—") return "";
  if (text.length <= 4) return text;
  return text.slice(-4);
}

async function approvedStationsByFingerprint(fingerprints: string[]) {
  const unique = [...new Set(fingerprints.map((value) => value.trim().toLowerCase()).filter((value) => /^[0-9a-f]{64}$/.test(value)))];
  const found = new Map<string, { storeId: string; name: string; stationId: string }[]>();
  for (let index = 0; index < unique.length; index += 30) {
    const group = unique.slice(index, index + 30);
    const snap = await getDocs(query(collectionGroup(db, "stations"), where("fingerprint", "in", group)));
    snap.docs.forEach((item) => {
      if (item.ref.parent.id !== "stations") return;
      const data = item.data() as { fingerprint?: string; status?: string; stationName?: string; posStationId?: string; stationId?: string };
      if (String(data.status || "") !== "APPROVED") return;
      const fingerprint = String(data.fingerprint || "").trim().toLowerCase();
      const storeId = item.ref.parent.parent?.id || "";
      const name = String(data.stationName || "").trim();
      const stationId = shownStationId({ id: item.id, posStationId: data.posStationId, stationId: data.stationId });
      if (!fingerprint || !storeId || !name) return;
      const list = found.get(fingerprint) || [];
      list.push({ storeId, name, stationId });
      found.set(fingerprint, list);
    });
  }
  return found;
}

const StoresManagement: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { allStores } = useStore();
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPlan, setFilterPlan] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [newestFirst, setNewestFirst] = useState(true);
  const [versions, setVersions] = useState<ProductVersion[]>([]);
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [stationRows, setStationRows] = useState<Record<string, StationListRow[]>>({});
  const [stationsReady, setStationsReady] = useState(false);
  const [pageTab, setPageTab] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const storeNamesRef = useRef(new Map<string, string>());
  storeNamesRef.current = new Map(allStores.map((store) => [store.id, store.name || store.id]));

  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("#d5e4ea", "gray.600");
  const textSecondary = useColorModeValue("gray.500", "gray.400");
  const storeColumns = "minmax(0, 3fr) minmax(0, 0.9fr) minmax(0, 1fr) minmax(0, 0.8fr) minmax(0, 0.7fr) minmax(0, 1.1fr) minmax(0, 0.8fr) minmax(0, 1fr) minmax(0, 0.8fr) minmax(0, 0.8fr)";

  useEffect(() => {
    getDocs(collection(db, PRODUCT_VERSIONS_COLLECTION))
      .then((snap) => {
        setVersions(snap.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<ProductVersion, "id">) })));
      })
      .catch(() => setVersions([]));
  }, []);

  const filteredStores = useMemo(() => {
    return allStores.filter((store) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (
          !store.name?.toLowerCase().includes(query) &&
          !store.email?.toLowerCase().includes(query) &&
          !store.id?.toLowerCase().includes(query) &&
          !store.phone?.toLowerCase().includes(query)
        ) {
          return false;
        }
      }

      // Plan filter
      if (filterPlan !== "all") {
        if (store.subscriptionPlan !== filterPlan) {
          return false;
        }
      }

      // Status filter
      if (filterStatus !== "all") {
        if (store.subscriptionStatus !== filterStatus) {
          return false;
        }
      }

      return true;
    }).sort((left, right) => {
      const diff = createdMillis(right) - createdMillis(left);
      return newestFirst ? diff : -diff;
    });
  }, [allStores, searchQuery, filterPlan, filterStatus, newestFirst]);

  const pageCount = Math.max(1, Math.ceil(filteredStores.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageStores = filteredStores.slice(safePage * pageSize, (safePage + 1) * pageSize);
  const pageKey = pageStores.map((store) => store.id).join("|");
  const pageStoresRef = useRef(pageStores);
  pageStoresRef.current = pageStores;

  useEffect(() => {
    setPage(0);
  }, [searchQuery, filterPlan, filterStatus, pageSize, newestFirst]);

  useEffect(() => {
    setStationsReady(false);
  }, [pageKey]);

  useEffect(() => {
    let cancelled = false;
    const loadStations = async () => {
      const stores = pageStoresRef.current;
      if (!stores.length) {
        setStationRows({});
        setStationsReady(true);
        return;
      }
      const entries = await Promise.all(stores.map(async (store) => {
        const storeRecord = store as Store & { maxStations?: number; activeStationCount?: number };
        const [stationSnap, requestSnap, loginSnap] = await Promise.all([
          getDocs(collection(db, "stores", store.id, "stations")),
          getDocs(query(collection(db, "stationRegistrationRequests"), where("restaurantId", "==", store.id))),
          getDocs(collection(db, "stores", store.id, "stationLogins")),
        ]);
        const limitCount = typeof storeRecord.maxStations === "number" ? storeRecord.maxStations : 1;
        const activeCount = Number(storeRecord.activeStationCount || 0);
        const atLimit = activeCount >= limitCount;
        const stations: StationDoc[] = stationSnap.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<StationDoc, "id">) }));
        const logins = new Map(loginSnap.docs.map((item) => {
          const data = item.data() as { posUserId?: string; posUserName?: string };
          return [item.id, { posUserId: String(data.posUserId || ""), posUserName: String(data.posUserName || "") }] as const;
        }));
        const fingerprints = new Set(stations.map((item) => String(item.fingerprint || "")).filter(Boolean));
        const rows: StationListRow[] = stations.map((item) => {
          const login = logins.get(item.id) || logins.get(String(item.stationId || "")) || logins.get(String(item.posStationId || ""));
          return {
            key: item.id,
            name: String(item.stationName || "Workstation"),
            stationId: shownStationId(item),
            fingerprint: String(item.fingerprint || "").trim().toLowerCase(),
            sharedNames: [],
            connected: stationIsConnected(item),
            overCapacity: false,
            loginPosUserId: login?.posUserId || "",
            loginPosUserName: login?.posUserName || "",
          };
        });
        requestSnap.docs.forEach((item) => {
          const data = item.data() as { status?: string; fingerprint?: string; stationName?: string };
          if (data.status !== "PENDING" || !atLimit) return;
          if (data.fingerprint && fingerprints.has(data.fingerprint)) return;
          rows.push({
            key: item.id,
            name: String(data.stationName || "Workstation"),
            stationId: "",
            fingerprint: String(data.fingerprint || "").trim().toLowerCase(),
            sharedNames: [],
            connected: false,
            overCapacity: true,
            loginPosUserId: "",
            loginPosUserName: "",
          });
        });
        return [store.id, rows] as const;
      }));
      const shared = await approvedStationsByFingerprint(entries.flatMap(([, rows]) => rows.map((row) => row.fingerprint))).catch(() => new Map<string, { storeId: string; name: string; stationId: string }[]>());
      entries.forEach(([storeId, rows]) => {
        rows.forEach((row) => {
          const names = (shared.get(row.fingerprint) || [])
            .filter((item) => item.storeId !== storeId)
            .map((item) => {
              const storeName = storeNamesRef.current.get(item.storeId) || item.storeId;
              const mark = lastFour(item.stationId);
              return mark ? `${item.name} *${mark} ${storeName}` : `${item.name} ${storeName}`;
            });
          row.sharedNames = [...new Set(names)];
        });
      });
      if (!cancelled) {
        setStationRows(Object.fromEntries(entries));
        setStationsReady(true);
      }
    };
    loadStations().catch(() => {
      if (!cancelled) {
        setStationRows({});
        setStationsReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [pageKey]);

  const openStore = (store: Store) => {
    navigate(`/restaurants/${store.id}`);
  };

  const versionName = (store: Store) => {
    const assigned = versions.find((item) => item.id === store.productVersionId);
    const fallback = versions.find((item) => item.isDefault) || versions[0];
    return productVersionLabel(assigned || fallback);
  };

  const handleLoginAs = async (store: Store) => {
    if (store.subscriptionStatus === "deactivated") {
      toast({
        title: "This store is deactivated",
        description: "A deactivated store cannot be opened.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (!currentUser?.email) {
      toast({
        title: "Error",
        description: "User not authenticated",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Add super admin to store's admins array
      const storeRef = doc(db, "stores", store.id);
      await updateDoc(storeRef, {
        admins: arrayUnion(currentUser.email),
        updated: serverTimestamp(),
      });

      const storeUrl = `https://back.nuagepos.com/#/admin/setting?store=${encodeURIComponent(store.id)}`;
      window.open(storeUrl, "_blank");

      toast({
        title: "Opening Store",
        description: `Opening ${store.name}.`,
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error granting access:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to grant access",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case "active":
        return "green";
      case "past_due":
        return "yellow";
      case "canceled":
        return "red";
      case "deactivated":
        return "red";
      case "trialing":
        return "blue";
      default:
        return "gray";
    }
  };

  return (
    <PageShell
      title="Stores"
      description={pageTab === 0
        ? "Open a store to update its summary, or log in to work inside the store."
        : "Copy one menu onto another store. Menus already on the destination stay in place."}
      fluid
    >
      <Tabs colorScheme="blue" variant="enclosed" index={pageTab} onChange={setPageTab}>
        <TabList>
          <Tab>Stores</Tab>
          <Tab>Menu copy</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
      <VStack spacing={6} align="stretch">

        {/* Filters */}
        <HStack spacing={4} flexWrap="wrap">
          <Button colorScheme="blue" onClick={() => setCreateOpen(true)}>Create new store</Button>
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search stores..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg={bgCard}
            />
          </InputGroup>

          <Select
            value={filterPlan}
            onChange={(e) => setFilterPlan(e.target.value)}
            maxW="200px"
            bg={bgCard}
          >
            <option value="all">All Plans</option>
            <option value="essential">Essential</option>
            <option value="standard">Standard</option>
            <option value="pro_kitchen">Pro Kitchen</option>
            <option value="elite_kitchen">Elite Kitchen</option>
          </Select>

          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            maxW="200px"
            bg={bgCard}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="canceled">Canceled</option>
            <option value="past_due">Past Due</option>
            <option value="trialing">Trialing</option>
            <option value="deactivated">Deactivated</option>
          </Select>

          <Button
            variant="outline"
            bg={bgCard}
            px={3}
            aria-label={newestFirst ? "Newest stores first" : "Oldest stores first"}
            title={newestFirst ? "Newest first" : "Oldest first"}
            onClick={() => setNewestFirst((current) => !current)}
          >
            <HStack spacing={2}>
              {React.createElement(FiClock as React.ComponentType<any>, { size: 17 })}
              {React.createElement((newestFirst ? FiArrowDown : FiArrowUp) as React.ComponentType<any>, { size: 17 })}
            </HStack>
          </Button>
        </HStack>

        {/* Stores Table */}
        <VStack align="stretch" spacing={3}>
          <Grid templateColumns={storeColumns} px={4} columnGap={2}>
            {["Store", "Store type", "Plan", "Status", "Price", "Version", "Mode", "Owner", "Admins", "Actions"].map((label) => (
              <Text key={label} fontSize="xs" fontWeight="700" color={textSecondary} textTransform="uppercase">{label}</Text>
            ))}
          </Grid>
          {pageStores.length === 0 ? (
            <Box bg={bgCard} border="1px solid" borderColor={borderColor} borderRadius="lg" py={8}>
              <Text color={textSecondary} textAlign="center">
                {allStores.length === 0 ? "No stores found" : "No stores match the filters"}
              </Text>
            </Box>
          ) : (
            pageStores.map((store) => (
              <Box key={store.id} bg={bgCard} border="1px solid" borderColor={borderColor} borderRadius="lg" px={4} py={3}>
                <Grid templateColumns={storeColumns} alignItems="center" columnGap={2}>
                  <Text fontSize="sm" fontWeight="semibold" noOfLines={1} title={`${store.name || "N/A"} ${store.id}`}>
                    {store.name || "N/A"}
                    <Text as="span" fontSize="xs" color={textSecondary}> *{lastFour(store.id)}</Text>
                  </Text>
                  <Text fontSize="sm" noOfLines={1}>
                    {ACTIVITY_LABEL[billingSubactivityOf(store.billingSubactivity)]}
                  </Text>
                  <Text fontSize="sm" noOfLines={1}>{getPlanDisplayName(store.subscriptionPlan)}</Text>
                  <Badge colorScheme={getStatusColor(store.subscriptionStatus)} w="fit-content">
                    {store.subscriptionStatus === "deactivated"
                      ? "Deactivated"
                      : store.subscriptionStatus === "past_due"
                        ? "Past due"
                        : store.subscriptionStatus === "trialing"
                          ? "Trialing"
                          : store.subscriptionStatus === "canceled"
                            ? "Canceled"
                            : "Active"}
                  </Badge>
                  <Text fontSize="sm" whiteSpace="nowrap">
                    ${getPlanPrice(store.subscriptionPlan)}
                    {getPlanPrice(store.subscriptionPlan) > 0 ? "/mo" : ""}
                  </Text>
                  <Text fontSize="sm" noOfLines={1} title={versionName(store)}>{versionName(store)}</Text>
                  <Badge colorScheme={store.storeMode === "production" ? "green" : "orange"} w="fit-content">
                    {store.storeMode === "production" ? "Production" : "Sandbox"}
                  </Badge>
                  <Text fontSize="sm" noOfLines={1} title={store.createdBy}>{store.createdBy || "N/A"}</Text>
                  <Text fontSize="xs" noOfLines={2} title={(store.admins || []).join(", ")}>
                    {store.admins && store.admins.length > 0 ? store.admins.join(", ") : "None"}
                  </Text>
                  <HStack spacing={0}>
                    <IconButton
                      aria-label="View store"
                      icon={<ViewIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={() => openStore(store)}
                    />
                    <IconButton
                      aria-label="Edit store"
                      icon={<EditIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={() => openStore(store)}
                    />
                    <IconButton
                      aria-label="Login as store admin"
                      icon={React.createElement(FiLogIn as React.ComponentType<any>)}
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => handleLoginAs(store)}
                      title="Login as - Grant admin access to this store"
                    />
                  </HStack>
                </Grid>
                <Box pl="3ch" pt={2}>
                  {!stationsReady ? (
                    <Text fontSize="xs" color={textSecondary}>Loading stations</Text>
                  ) : (stationRows[store.id] || []).length === 0 ? (
                    <Text fontSize="xs" color={textSecondary}>No stations</Text>
                  ) : (
                    <HStack spacing={8} align="start" w="full">
                      {(stationRows[store.id] || []).map((row) => (
                        <VStack key={row.key} spacing={0} align="stretch" flex="1" minW="0">
                          <HStack spacing={2} minW="0">
                            <Text fontSize="sm" fontWeight="600" noOfLines={1}>{row.name}</Text>
                            {lastFour(row.stationId) && (
                              <Text fontSize="xs" color={textSecondary} title={row.stationId} flexShrink={0}>*{lastFour(row.stationId)}</Text>
                            )}
                            {row.overCapacity ? (
                              <Badge colorScheme="orange" flexShrink={0}>Over capacity</Badge>
                            ) : (
                              <Badge bg={row.connected ? "green.500" : "gray.200"} color={row.connected ? "white" : "gray.700"} flexShrink={0}>
                                {row.connected ? "Connected" : "Not connected"}
                              </Badge>
                            )}
                            {row.loginPosUserId ? (
                              <Text fontSize="sm" title={row.loginPosUserId} noOfLines={1}>
                                {row.loginPosUserName || "POS user"}
                                <Text as="span" fontSize="xs" color={textSecondary}> *{lastFour(row.loginPosUserId)}</Text>
                              </Text>
                            ) : (
                              <Text fontSize="sm" color={textSecondary} noOfLines={1}>No POS user</Text>
                            )}
                          </HStack>
                          {row.sharedNames.length > 0 && (
                            <Text fontSize="xs" color={textSecondary} noOfLines={1}>{row.sharedNames.join(", ")}</Text>
                          )}
                        </VStack>
                      ))}
                    </HStack>
                  )}
                </Box>
              </Box>
            ))
          )}
        </VStack>

        <HStack justify="space-between" flexWrap="wrap" spacing={3}>
          <Text fontSize="sm" color={textSecondary}>
            Total Stores: {allStores.length} | Filtered: {filteredStores.length}
          </Text>
          <HStack spacing={3}>
            <Select
              value={String(pageSize)}
              onChange={(event) => setPageSize(Number(event.target.value))}
              maxW="120px"
              bg={bgCard}
              aria-label="Stores per page"
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="500">500</option>
            </Select>
            <Button size="sm" variant="outline" isDisabled={safePage === 0} onClick={() => setPage(safePage - 1)}>Previous</Button>
            <Text fontSize="sm" color={textSecondary}>Page {safePage + 1} of {pageCount}</Text>
            <Button size="sm" variant="outline" isDisabled={safePage + 1 >= pageCount} onClick={() => setPage(safePage + 1)}>Next</Button>
          </HStack>
        </HStack>
      </VStack>
          </TabPanel>
          <TabPanel px={0}>
            <MenuCopyTab stores={allStores} />
          </TabPanel>
        </TabPanels>
      </Tabs>
      <CreateStoreDialog
        open={createOpen}
        stores={allStores}
        ownerEmail={currentUser?.email || ""}
        onClose={() => setCreateOpen(false)}
      />
    </PageShell>
  );
};

export default StoresManagement;
